import { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import CustomForm from '../../../core/components/core/Util/Form/CustomForm/CustomForm';
import CustomFormContainer from '../../../core/components/core/Util/Form/CustomForm/CustomFormContainer';
import config from '../../config/config';
import Fetch from '../../../core/util/core/Fetch';
import { chooseProfileFilter } from '../../helpers/chooseProfileFilter';
import sleep from 'sleep-promise';

function Recurring() {
    const navigate = useNavigate();
    const profileFilter = chooseProfileFilter();
    const [formContent, setFormContent] = useState(<>Carregando...</>);

    const endpointBackend = config.endpoints.backend;
    const paths = {
        urlGoBack: '/gci/expenses/list',
        endpointSave: `${endpointBackend}gci/expenses/save-recurring`,
        endpointGetAllExpenseTypes: `${endpointBackend}gci/expense-types/getall`,
        endpointGetAllAccountingPlans: `${endpointBackend}gci/accounting-plans/getall`,
        endpointGetAllDocumentTypes: `${endpointBackend}gci/revenues-document-types/getall`,
        endpointGetAllSuppliers: `${endpointBackend}gci/suppliers/getall`,
        endpointGetAllPaymentMethods: `${endpointBackend}gci/payment-methods/getall`,
        endpointGetAllCompanies: `${endpointBackend}gci/companies/getall`,
        endpointGetAllVentures: `${endpointBackend}gci/ventures/getall`,
        endpointGetAllProjects: `${endpointBackend}gci/projects/getall`,
    };

    const hiddenValues = {};
    if (profileFilter.gci_company_id) hiddenValues.gci_company_id = profileFilter.gci_company_id;

    const getOrganizationType = async (values) => {
        if (profileFilter.organization_type) return profileFilter.organization_type;
        if (!values?.gci_company_id) return 'default';
        const company = await Fetch.post(`${endpointBackend}gci/companies/getById`, { id: values.gci_company_id });
        return company?.organization_type || 'default';
    };

    const getDocumentNumberRequired = async (values) => {
        if (!values?.gci_revenue_document_type_id) return false;
        const documentType = await Fetch.post(`${endpointBackend}gci/revenues-document-types/getById`, { id: values.gci_revenue_document_type_id });
        return documentType?.need_document_number === '1';
    };

    const rerender = async (values) => {
        const form = getForm(values, await getOrganizationType(values), await getDocumentNumberRequired(values));
        setFormContent(<></>);
        await sleep(1);
        setFormContent(<CustomFormContainer form={form} content={<CustomForm form={form} />} />);
    };

    const companyId = (values) => profileFilter.gci_company_id || values.gci_company_id || null;

    const getForm = (values, organizationType = 'default', documentNumberRequired = false) => ({
        hiddenValues,
        afterSubmit: () => navigate(paths.urlGoBack),
        labelTitleAdd: 'Despesa Recorrente',
        fields: [
            !profileFilter.gci_company_id ? {
                name: 'gci_company_id',
                type: 'select',
                label: 'Empresa',
                options: [],
                optionsEndpoint: { endpoint: paths.endpointGetAllCompanies, label: 'name', value: 'id' },
                validations: { required: true },
                handleChange: (element, field, values) => rerender(values),
                columnSize: 12
            } : {},
            organizationType === 'default' ? {
                name: 'gci_venture_id',
                type: 'select',
                label: 'Empreendimento',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllVentures,
                    label: 'enterprise_name',
                    value: 'id',
                    params: { filter: { ...profileFilter, gci_company_id: companyId(values) } }
                },
                validations: { required: true },
                columnSize: 12
            } : {
                name: 'gci_project_id',
                type: 'select',
                label: 'Projeto',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllProjects,
                    label: 'project_name',
                    value: 'id',
                    params: { filter: { gci_company_id: companyId(values) } }
                },
                validations: { required: true },
                columnSize: 12
            },
            {
                name: 'gci_expense_type_id',
                type: 'select',
                label: 'Tipo de Despesa',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllExpenseTypes,
                    label: 'name',
                    value: 'id',
                    params: { filter: { ...profileFilter } }
                },
                validations: { required: true },
                columnSize: 4
            },
            {
                name: 'gci_accounting_plan_id',
                type: 'select',
                label: 'Plano de Contas',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllAccountingPlans,
                    label: 'full_path',
                    value: 'id',
                    params: { filter: { ...profileFilter, gci_company_id: companyId(values), onlyLastLevel: true } }
                },
                validations: { required: true },
                columnSize: 8
            },
            {
                name: 'gci_revenue_document_type_id',
                type: 'select',
                label: 'Tipo Doc',
                options: [],
                optionsEndpoint: { endpoint: paths.endpointGetAllDocumentTypes, label: 'name', value: 'id' },
                validations: { required: false },
                handleChange: (element, field, values) => rerender(values),
                columnSize: 4
            },
            {
                name: 'document_number',
                type: 'text',
                label: 'Número do Doc',
                validations: { required: documentNumberRequired },
                columnSize: 4
            },
            {
                name: 'gci_supplier_id',
                type: 'select',
                label: 'Fornecedor',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllSuppliers,
                    label: 'supplier_name',
                    value: 'id',
                    params: { filter: { gci_company_id: companyId(values) } }
                },
                validations: { required: true },
                columnSize: 4
            },
            {
                name: 'description',
                type: 'text',
                label: 'Descrição',
                validations: { required: false },
                columnSize: 12
            },
            {
                name: 'installments_series',
                type: 'multiinput',
                label: 'Despesas',
                sortBy: 'index',
                form: [
                    {
                        name: 'gci_payment_method_id',
                        type: 'select',
                        label: 'Forma de Pagamento',
                        options: [],
                        optionsEndpoint: {
                            endpoint: paths.endpointGetAllPaymentMethods,
                            label: 'name',
                            value: 'id',
                            params: { filter: { type: ['expense', 'both'] } }
                        },
                        validations: { required: false },
                        styleTh: { width: '20%' }
                    },
                    { name: 'estimated_value', type: 'decimal', label: 'Valor Estimado', validations: { required: true } },
                    { name: 'value', type: 'decimal', label: 'Valor da Despesa', validations: { required: false } },
                    { name: 'emission_date', type: 'date', label: 'Data de Emissão (1ª parcela)', validations: { required: true } },
                    { name: 'due_date', type: 'date', label: 'Data de Vencimento (1ª parcela)', validations: { required: true } },
                    { name: 'periodicity', type: 'integer', label: 'Periodicidade (dias)', validations: { required: true }, styleTh: { width: '10%' } },
                    { name: 'number_of_installments', type: 'integer', label: 'Parcelas', validations: { required: true }, styleTh: { width: '9%' } },
                ],
                validations: { required: true },
                columnSize: 12
            },
        ],
        labelSubmitButton: 'Salvar',
        values,
        ...paths,
    });

    useEffect(() => {
        rerender({});
    }, []);

    return <div>{formContent}</div>;
}

export default Recurring;
